import { Link } from 'react-router-dom';
import MarketingLayout from '../components/marketing/MarketingLayout';
import '../styles/marketing-features.css';

/* ============================================================
   Clarid /features — deep, per-module capability reference.
   Restyled onto the banker-paper design system; the module
   copy (titles, headlines, descriptions, feature lists, who
   it's for, regulations) is preserved from the prior page.
   ============================================================ */

interface Capability {
  id: string;
  badge: string;
  title: string;
  headline: string;
  description: string;
  features: string[];
  keyFor: string[];
  regulations: string[];
}

const capabilities: Capability[] = [
  {
    id: 'marketing-compliance',
    badge: 'Professional Tier',
    title: 'Marketing Compliance',
    headline: 'Every marketing piece. Every regulation. Every time.',
    description: 'AI-powered content review that checks marketing materials against FDIC, NCUA, Reg DD, Reg Z, UDAAP, and federal disclosure requirements. Multi-channel governance for print, digital, social media, and website content with full lifecycle tracking from creation to expiration.',
    features: [
      'AI content analysis with specific regulatory rule citations for every finding',
      'UDAAP language risk detection across all marketing channels',
      'Social media compliance monitoring across platforms',
      'Multi-channel content governance with lifecycle tracking',
      'Approval workflows with compliance officer sign-off and escalation',
      'Examiner-ready PDF exports with immutable, timestamped audit trail',
    ],
    keyFor: ['Compliance Officers', 'Marketing Teams', 'Branch Managers'],
    regulations: ['FDIC Advertising Rules', 'NCUA Advertising Rules', 'Reg DD', 'Reg Z', 'UDAAP', 'FHA', 'ECOA'],
  },
  {
    id: 'hmda-basics',
    badge: 'Professional Tier',
    title: 'HMDA Basics',
    headline: 'Upload your LAR. Know exactly what’s wrong.',
    description: 'LAR file validation with 160+ edit checks covering syntactical, validity, quality, and macro edits. Plain English explanations for every failure with suggested fixes. Data quality monitoring dashboard to track HMDA data integrity throughout the year.',
    features: [
      'LAR file upload and validation with 160+ edit checks',
      'Plain English explanations for every edit failure with fix suggestions',
      'Data quality monitoring dashboard with trend tracking',
      'Validation history and progress tracking',
    ],
    keyFor: ['Compliance Officers', 'CRA Officers', 'Operations Teams'],
    regulations: ['HMDA / Reg C', 'FFIEC Reporting Standards'],
  },
  {
    id: 'hmda-full-suite',
    badge: 'Enterprise Tier',
    title: 'Full HMDA Suite',
    headline: 'The complete HMDA pipeline — from validation to CFPB filing.',
    description: 'Everything in HMDA Basics plus advanced tools for the full filing lifecycle. Geocoding validation against FFIEC census data, APOR rate spread calculations, edit check intelligence with root cause analysis and pattern detection, loan officer monitoring, and direct CFPB filing preparation.',
    features: [
      'Edit check intelligence with root cause analysis and pattern detection',
      'Census tract geocoding validation against FFIEC data',
      'APOR rate spread calculator with current rate tables',
      'Loan officer monitoring with performance benchmarking',
      'CFPB filing via Login.gov OAuth with direct API submission',
    ],
    keyFor: ['Compliance Officers', 'CRA Officers', 'Operations Teams'],
    regulations: ['HMDA / Reg C', 'CFPB Filing Requirements', 'FFIEC Reporting Standards'],
  },
  {
    id: 'fair-lending',
    badge: 'Enterprise Tier',
    title: 'Fair Lending Analysis',
    headline: 'The monitoring program examiners expect to see.',
    description: 'Continuous fair lending monitoring aligned to all 17 IFLEP risk factors. Statistical analysis across 9 prohibited basis groups and 6 loan purposes covering denial disparities, pricing, processing time, steering, redlining, and marketing. Loan officer performance monitoring with peer comparisons. Exception tracking with demographic disparity analysis. Board-ready risk reports generated automatically.',
    features: [
      'Full IFLEP Fair Lending Dashboard — all 17 risk factors across 9 prohibited basis groups',
      "Denial, pricing, processing time & steering disparity analysis with chi-square, Fisher's exact, and t-tests",
      'Redlining analysis with MMCT classification, geographic mapping, and heat maps',
      'Loan officer performance monitoring with peer benchmarking',
      'Exception tracking with demographic disparity analysis',
      'Automated fair lending risk assessments with board-ready reports',
    ],
    keyFor: ['Compliance Officers', 'Fair Lending Officers', 'Senior Management'],
    regulations: ['ECOA / Reg B', 'Fair Housing Act', 'CRA', 'UDAAP'],
  },
  {
    id: 'regression-analysis',
    badge: 'Enterprise Tier',
    title: 'Regression Analysis Engine',
    headline: 'The statistical analysis examiners run — now you run it first.',
    description: 'IFLEP-aligned logistic and OLS regression models that analyze denial rates, pricing disparities, and FHA steering patterns across your HMDA data. Produces odds ratios, basis point impacts, matched-pair comparisons, and AI-generated narratives — calibrated to your primary regulator (FDIC, OCC, Fed, NCUA). Runs automatically on every HMDA upload and quarterly on schedule.',
    features: [
      'Logistic regression for denial and steering with odds ratios and confidence intervals',
      'OLS regression for pricing with basis point impact and rate spread analysis',
      'Matched-pair analysis for examiner file review with similarity scoring',
      'Regulator-aware output calibrated to FDIC, OCC, Fed, or NCUA exam standards',
      'Auto-triggers on HMDA upload; quarterly scheduled runs with threshold monitoring',
    ],
    keyFor: ['Compliance Officers', 'Fair Lending Officers', 'Senior Management', 'Board of Directors'],
    regulations: ['ECOA / Reg B', 'Fair Housing Act', 'IFLEP', 'Reg C / HMDA'],
  },
  {
    id: 'cra-module',
    badge: 'Enterprise Tier',
    title: 'CRA Module',
    headline: 'CRA performance, measured and exam-ready.',
    description: 'Comprehensive CRA analysis built on your HMDA data. Lending distribution analysis by borrower income, geography, and demographics. Community development activity tracking with categorization. Assessment area management with census tract mapping. CRA exam readiness scoring with gap analysis and public file generation.',
    features: [
      'Lending analysis by income level, geography, and borrower demographics',
      'Community development activity tracking and categorization',
      'Assessment area management with census tract mapping',
      'CRA exam readiness scoring with actionable gap analysis',
      'Public file generation and year-over-year trend analysis',
    ],
    keyFor: ['CRA Officers', 'Compliance Officers', 'Senior Management'],
    regulations: ['Community Reinvestment Act', 'CRA Exam Procedures', 'FFIEC CRA Guidelines'],
  },
  {
    id: 'whole-loan-book',
    badge: 'Enterprise Tier',
    title: 'Whole-Loan-Book Fair Lending & CRA (ALERT / ILDR)',
    headline: 'Fair lending and CRA on the loans that never reach a LAR.',
    description: 'Most tools stop at the HMDA LAR. Clarid ingests your loan trial balance — the ALERT / ILDR file — and runs the same fair-lending and CRA analysis on consumer, small-business, and small-farm loans. That is the book community banks and ISBs were never able to analyze, because there is no HMDA equivalent for it. Reproduces the Federal Reserve Fair Lending Tool methodology to the decimal, now on the non-HMDA book.',
    features: [
      'Fixed-width ALERT / ILDR (or Excel) ingest — minutes, not hours of manual Excel parsing',
      'Name-based sex & ethnicity proxy (FRB and FDIC methodologies), reproduced to the decimal',
      'RC-C loan-type classification — consumer (6-series), small business, and small farm',
      'Feeds the same redlining, CRA distribution, and exam-packaging surfaces as your HMDA data',
      'PII-safe by construction: SSN dropped at ingest; names and addresses encrypted at rest',
    ],
    keyFor: ['Compliance Officers', 'Fair Lending Officers', 'CRA Officers', 'Consultants & Advisors'],
    regulations: ['ECOA / Reg B', 'Fair Housing Act', 'CRA', 'FFIEC Interagency Fair Lending'],
  },
  {
    id: 'exam-readiness',
    badge: 'Professional Tier',
    title: 'Exam Readiness',
    headline: 'Know where you stand before the examiner arrives.',
    description: 'Continuous exam readiness scoring that evaluates document completeness, open findings, overdue tasks, and policy review status. Get a clear readiness score for your next exam with actionable recommendations to close gaps before they become findings.',
    features: [
      'Exam readiness scoring with weighted assessment across compliance areas',
      'Document completeness tracking against exam requirements',
      'Open findings and overdue items monitoring with alerts',
      'AI-powered recommendations to improve readiness score',
    ],
    keyFor: ['Compliance Officers', 'CCOs', 'Senior Management'],
    regulations: ['FDIC Exam Procedures', 'NCUA Exam Procedures', 'State Regulator Requirements'],
  },
  {
    id: 'exam-center',
    badge: 'Enterprise Tier',
    title: 'Exam Center',
    headline: 'Everything examiners want to see, organized the way they want to see it.',
    description: "End-to-end exam lifecycle management from pre-exam readiness assessment through post-exam remediation. Automated document package generation, examiner request tracking, findings management with root cause analysis, and remediation workflows with deadline tracking. Your compliance officer's exam command center.",
    features: [
      'Full exam lifecycle management with status tracking',
      'One-click exam document package generation (ZIP with XLSX, PDFs, audit trails)',
      'Examiner request tracking with response time monitoring',
      'Findings tracking with remediation workflows and deadlines',
      'Complete examiner-defensible audit trail across all modules',
    ],
    keyFor: ['Compliance Officers', 'CCOs', 'Senior Management', 'Board of Directors'],
    regulations: ['FDIC Exam Procedures', 'NCUA Exam Procedures', 'State Regulator Requirements'],
  },
  {
    id: 'regulatory-monitoring',
    badge: 'Professional Tier',
    title: 'Regulatory Monitoring',
    headline: 'Stay ahead of regulatory changes.',
    description: "Automated monitoring of regulatory changes from federal and state agencies. Each update is analyzed and mapped to your institution's specific products and operations with prioritized action items and compliance deadlines. Covers state-specific compliance rules across all operating states.",
    features: [
      'Regulatory change monitoring with automatic categorization',
      'State-specific compliance rule management across all operating states',
      'Prioritized action items with deadlines and assignable tasks',
      'Impact assessment mapped to your institution’s products and operations',
    ],
    keyFor: ['Compliance Officers', 'CCOs', 'Legal Teams'],
    regulations: ['Federal Register Monitoring', 'FDIC FILs', 'NCUA Letters', 'State Regulatory Updates'],
  },
  {
    id: 'enforcement-intelligence',
    badge: 'Enterprise Tier',
    title: 'Enforcement Intelligence',
    headline: 'Know before the examiner tells you.',
    description: 'Enforcement action monitoring across FDIC, OCC, CFPB, NCUA, and DOJ with "Am I Exposed?" analysis that maps each action to your institution\'s operations. Trend analysis shows enforcement patterns by agency, violation type, and institution size to help you anticipate examiner focus areas.',
    features: [
      'Enforcement action tracking across all federal regulators and DOJ',
      '"Am I Exposed?" analysis mapped to your institution’s operations',
      'Enforcement trend analysis by agency, violation type, and institution size',
      'Peer comparison and early warning alerts for relevant actions',
    ],
    keyFor: ['Compliance Officers', 'CCOs', 'Legal Teams', 'Senior Management'],
    regulations: ['Federal Register Monitoring', 'FDIC FILs', 'NCUA Letters', 'OCC Bulletins'],
  },
  {
    id: 'complaints-disputes',
    badge: 'Professional + Enterprise',
    title: 'Complaints & Disputes',
    headline: 'Complaints, disputes, and the patterns examiners look for.',
    description: 'Track every complaint and dispute with Reg E SLA timers, AI-assisted categorization, provisional credit workflows, and CFPB integration. The intelligence layer batch-analyzes the full record to surface themes, spikes, and risk patterns — not just a per-complaint log. Vendor-aware CSV importer brings historical complaint records from Ncontracts, nCino, Wolters, Crowe, and ComplyPilot in one upload.',
    features: [
      'Reg E dispute SLA tracking with provisional-credit deadlines',
      'AI categorization + deterministic rules engine risk scoring',
      'CFPB complaint feed integration and reference tracking',
      'Batch theme analysis with spikes detection across the full history',
      'Vendor-aware CSV importer for migration from other compliance vendors',
    ],
    keyFor: ['Compliance Officers', 'Consumer Operations', 'Risk Management'],
    regulations: ['Regulation E', 'UDAAP', 'CFPB Bulletin 2013-07 (complaint handling)'],
  },
  {
    id: 'bonds-analysis',
    badge: 'Channel Partners',
    title: 'Bond Analysis · CRA Qualification',
    headline: 'Per-bank precision for Freddie / Fannie multifamily bonds.',
    description: 'Built for fixed-income desks placing CRA-qualifying bonds with community-bank clients. Automated CUSIP-to-deal resolution via OpenFIGI, Offering Circular parsing for affordability and regulatory agreements, geocoded per-property assessment-area matching, and white-label Pitch PDF generation. Per-bank, in seconds. Freddie Multi PCs and Fannie DUS single-property live today; K-Deal multi-property and MSIA ingest in Q3 2026.',
    features: [
      'CUSIP-level analysis with full reasoning trail per property',
      'Per-bank CRA qualification against the bank’s assessment areas',
      'Map view + drill-down with per-property qualification status',
      'White-label or co-branded Pitch PDF deliverables',
      'REST API + Model Context Protocol for portfolio system embedding',
    ],
    keyFor: ['Fixed Income Desks', 'Investment Banking', 'CRA Officers'],
    regulations: ['CRA Investment Test (12 CFR §228.23)', 'Federal Reserve §208.26'],
  },
  {
    id: 'migration-kit',
    badge: 'All tiers',
    title: 'Migration & Onboarding',
    headline: 'Switch from your current vendor without the friction.',
    description: 'Three bulk importers absorb your historical compliance record from any incumbent vendor. The Data Export Rights brief walks your legal and vendor-management teams through exactly what to request and which contract clauses to invoke. HMDA, branches, and assessment areas land within 48 hours; policies, complaints, and exam findings within 5 business days.',
    features: [
      'Bulk PDF policy importer with AI metadata extraction',
      'Vendor-aware CSV importer for complaints (5 vendors + Generic)',
      'Exam findings importer with prior-finding recurrence preserved',
      '48-hour ingest for HMDA, branches, assessment areas',
      'Data Export Rights brief for circulation to legal and vendor management',
    ],
    keyFor: ['CCOs evaluating vendor changes', 'M&A integration teams', 'Vendor managers'],
    regulations: ['Data ownership and portability clauses (standard MSA)'],
  },
  {
    id: 'compliance-program',
    badge: 'Professional Tier',
    title: 'Compliance Program Management',
    headline: 'Your compliance program, running itself.',
    description: 'The daily command center for compliance officers. Compliance calendar with recurring task management, training program tracking, automated board report PDF generation, AI-powered policy gap analysis, regulation library, and compliance health scoring. Enterprise adds peer benchmarking against CFPB data.',
    features: [
      'Compliance calendar with recurring tasks and deadline alerts',
      'Training management with completion tracking',
      'Board report PDF generation with executive dashboards',
      'AI-powered policy gap analysis & version tracking',
      'Regulation library & compliance health scoring',
    ],
    keyFor: ['Compliance Officers', 'CCOs', 'Board of Directors'],
    regulations: ['BSA/AML Compliance', 'CMS Requirements', 'Board Reporting Standards'],
  },
];

const professionalFeaturesList = [
  'AI-powered marketing content review',
  'FDIC, NCUA, Reg DD, Reg Z, UDAAP checks',
  'Content library with lifecycle tracking',
  'Approval workflows with audit trail',
  'Website compliance scanning',
  'Social media compliance monitoring',
  'HMDA LAR builder + validation (170+ CFPB edit checks)',
  'HMDA data quality monitoring',
  'Complaints & disputes module with Reg E SLA tracking',
  'Bulk importers for vendor migration (policies, complaints, exam findings)',
  'Regulatory change monitoring & alerts',
  'State-specific compliance rules',
  'Exam readiness scoring & assessment',
  'Compliance calendar & task management',
  'Training management & completion tracking',
  'Board report PDF generation',
  'AI-powered policy gap analysis & version tracking',
  'Regulation library & compliance hub',
  'Complaint management & tracking',
  'AI copilot access (included features)',
  'Examiner-ready PDF exports',
  'Immutable audit logs',
];

const enterpriseAddOns = [
  'Edit check intelligence & auto-fix',
  'Geocoding validation',
  'APOR rate spread calculator',
  'CFPB filing via Login.gov OAuth',
  'Loan officer monitoring',
  'IFLEP Fair Lending Dashboard (17 risk factors)',
  'Regression analysis engine (denial, pricing, steering)',
  'Matched-pair analysis for examiner file review',
  'Redlining analysis',
  'Fair lending risk assessments',
  'Exception tracking',
  'CRA lending analysis & exam readiness',
  'Community development activity tracking',
  'Assessment area management',
  'Full exam lifecycle management',
  'One-click exam packages',
  'Findings & remediation tracking',
  'Enforcement action monitoring',
  '"Am I Exposed?" analysis',
  'Peer benchmarking',
  'Vendor compliance management',
  'Custom compliance rules engine (per-institution overlays)',
  'Bond Analysis module — CRA qualification for Freddie/Fannie multifamily',
  'Advanced MCP tools (30+ compliance tools across Claude / ChatGPT / Copilot)',
  'Cross-module data flow — HMDA → fair lending → CRA → board reports',
  'White-label / co-branded deliverables for channel partners',
  'Dedicated onboarding & support',
];

function tierClass(badge: string): string {
  return badge.startsWith('Enterprise') ? 'tier-ent' : 'tier-pro';
}

export default function FeaturesPage() {
  return (
    <MarketingLayout activeNav="features">
      {/* HERO */}
      <section className="feat-hero">
        <div className="container">
          <div className="kicker" style={{ marginBottom: '1.6rem' }}>§&nbsp;PLATFORM OVERVIEW</div>
          <h1 className="display balance" style={{ maxWidth: '20ch' }}>The complete compliance intelligence platform.</h1>
          <p className="lead pretty" style={{ marginTop: '1.6rem', maxWidth: '64ch' }}>
            Fourteen deeply integrated modules covering every aspect of community-bank and credit-union
            compliance — from marketing review to fair lending to CRA to exam day. Data flows between
            modules automatically: HMDA feeds fair lending, fair lending feeds regression, findings
            surface in board reports and exam packages.
          </p>
          <div className="hero-cta" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', marginTop: '2.2rem' }}>
            <Link to="/pricing" className="btn btn-primary">See Pricing <span className="arrow">→</span></Link>
            <Link to="/request-demo" className="btn btn-secondary">Request a demo</Link>
          </div>
        </div>
      </section>

      {/* CAPABILITY DEEP DIVES */}
      {capabilities.map((cap, idx) => (
        <section
          key={cap.id}
          id={cap.id}
          className={`section hairline-top${idx % 2 === 1 ? ' panel-bg' : ''}`}
        >
          <div className="container">
            <div className={`cap-grid reveal${idx % 2 === 1 ? ' rev' : ''}`}>
              <div>
                <span className={`pill cap-tier ${tierClass(cap.badge)}`}>{cap.badge}</span>
                <h2 className="cap-title">{cap.title}</h2>
                <p className="cap-headline">{cap.headline}</p>
                <p className="cap-desc">{cap.description}</p>
                <div className="cap-meta">
                  <div className="cap-meta-group">
                    <span className="cap-meta-label">Who it's for</span>
                    <div className="cap-tags">
                      {cap.keyFor.map((k) => <span className="chip" key={k}>{k}</span>)}
                    </div>
                  </div>
                  <div className="cap-meta-group">
                    <span className="cap-meta-label">Regulations</span>
                    <div className="cap-tags">
                      {cap.regulations.map((r) => <span className="chip" key={r}>{r}</span>)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="cap-features">
                <ul>
                  {cap.features.map((f) => (
                    <li key={f}><span className="ck">✓</span> {f}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* TWO-TIER SUMMARY */}
      <section className="section panel-bg hairline-top hairline-bottom">
        <div className="container">
          <div className="sign reveal" style={{ marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>
            <div className="sign-row">
              <span className="kicker kicker--ink">§&nbsp;WHAT'S IN EACH PLAN</span>
              <hr className="rule" />
            </div>
            <h2 className="h2 balance" style={{ maxWidth: '22ch' }}>Two plans, every module accounted for.</h2>
          </div>
          <div className="feat-tiers reveal">
            <div className="feat-tier">
              <div className="feat-tier-name">Professional</div>
              <p className="feat-tier-note">the connected compliance program</p>
              <ul>
                {professionalFeaturesList.map((f) => (
                  <li key={f}><span className="ck">✓</span> {f}</li>
                ))}
              </ul>
            </div>
            <div className="feat-tier feat-tier--ent">
              <div className="feat-tier-name">Enterprise</div>
              <p className="feat-tier-note">the full cross-module suite</p>
              <p className="feat-plus" style={{ marginTop: '1.4rem' }}>↳ Everything in Professional, plus:</p>
              <ul>
                {enterpriseAddOns.map((f) => (
                  <li key={f}><span className="ck">✓</span> {f}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="lead reveal" style={{ marginTop: 'clamp(2rem,3.5vw,3rem)', maxWidth: '52ch' }}>
            Not sure which fits? <Link to="/pricing" style={{ color: 'var(--teal-hover)', textDecoration: 'underline' }}>See pricing</Link> — both plans are sized to your institution on a call.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section final">
        <div className="container">
          <div className="reveal" style={{ maxWidth: '42ch' }}>
            <div className="kicker" style={{ color: 'var(--teal-bright)', marginBottom: '1.6rem' }}>§&nbsp;SEE IT ON YOUR DATA</div>
            <h2 className="h2 balance">Every module, working on your real workflows.</h2>
            <p className="lead" style={{ color: 'rgba(243,241,234,0.7)', marginTop: '1.5rem' }}>
              A guided walkthrough against your institution's actual compliance work — far more than a sandbox can show.
            </p>
            <div className="final-cta">
              <Link to="/pricing" className="btn btn-on-ink">See Pricing <span className="arrow">→</span></Link>
              <Link to="/request-demo" className="btn btn-on-ink-ghost">Request a demo</Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
